// The game host. Mounts a module, owns the ladder, the log, and the rewards.
// Also runs Town jobs (a round with a story and a bonus) and warms pet eggs.

import { h, clear } from '@kit/dom';
import { mulberry32, seedFrom } from '@kit/rng';
import { speak } from '@kit/tts';
import { play as playSound } from '@kit/audio';
import { go } from '../router';
import { GAME_BY_ID, lockInfo } from '../registry';
import { getSave, update, skill as skillState, totalStars, dayKey } from '../state';
import { applyAttempt, clampToGame, ringFor, SKILL_BY_ID } from '../skills';
import { roundReward, DOOR_CAP_PER_VISIT, DOOR_GEMS } from '../economy';
import { logAttempt, logRound } from '../log';
import { topBar, toast } from '../ui';
import type { AttemptEvent, GameContext, GameInstance, RoundSummary } from '../types';
import { CHARM_BY_ID } from '../charms';
import { findJob, giftFor, friendOfTheStreet, type TodayJob } from '../town';
import { regionIdFromKey, REGION_BY_ID } from '../regions';
import { DECOR_BY_ID } from '../decor';
import { eggForCharm, eggForStars, warmEggs, PET_TYPES } from '../pets';

export function playScreen(root: HTMLElement, params: string[]) {
  const mod = GAME_BY_ID[params[0]];
  if (!mod) { go('academy'); return; }

  let job: TodayJob | undefined;
  if (params[1] === 'job' && params[2]) {
    job = findJob(params[2]);
    if (!job || job.locked || job.done) { go(regionIdFromKey(params[2]) ? `region/${regionIdFromKey(params[2])}` : 'town'); return; }
  }
  // a neighbor's job lends her the game for the day, even if she has not unlocked it yet
  if (!job && !lockInfo(mod).unlocked) { go(mod.category === 'fun' ? 'wilds' : 'academy'); return; }

  const regionId = job ? regionIdFromKey(job.key) : null;
  const backTo = regionId ? `region/${regionId}` : job ? 'town' : mod.category === 'fun' ? 'wilds' : 'academy';
  let inst: GameInstance | null = null;
  let ended = false;
  let doorGems = 0;
  let roundIndex = 0;

  const host = h('div.game');
  const msg = h('div.msg');
  const bar = topBar(job ? job.job.title : mod.title, { back: () => { teardown(); go(backTo); } });
  root.append(bar, msg, host);

  function startRound() {
    clear(host);
    msg.textContent = '';
    msg.className = 'msg';
    ended = false;
    doorGems = 0;
    const primary = mod.skills[0];
    const save = getSave();
    const st = primary ? skillState(primary) : null;
    const pin = primary ? save.pins[primary] : undefined;
    const startLevel = st ? clampToGame(pinClamp(st.rung, pin), mod.levelRange) : mod.levelRange[0];
    let moved = 0;
    const seed = seedFrom(mod.id, Date.now(), roundIndex++);
    const t0 = performance.now();

    const ctx: GameContext = {
      level: startLevel,
      skill: primary ?? 'logic',
      currentLevel: () => {
        if (!st) return startLevel;
        return clampToGame(pinClamp(st.rung, getSave().pins[primary!]), mod.levelRange);
      },
      avatar: save.avatar,
      rng: mulberry32(seed),
      settings: save.settings,
      job: job ? { title: job.job.title, story: job.job.story, neighbor: job.neighbor.name } : undefined,
      pets: save.pets.pets.map(p => ({ id: p.id, name: p.name, type: p.type, stage: p.stage })),
      activePet: save.pets.active,
      report(e: AttemptEvent) {
        if (ended) return;
        const sk = e.skill || primary;
        if (sk && mod.category !== 'fun') {
          const s2 = skillState(sk);
          const r = applyAttempt(s2, e.correct, e.hintUsed, getSave().pins[sk]);
          if (r.moved) {
            moved += r.moved;
            if (r.moved > 0) {
              const ring = ringFor(r.after), prev = ringFor(r.before);
              if (prev.name !== ring.name) toast(`${SKILL_BY_ID[sk]?.kid ?? sk} ring is now ${ring.name}!`);
            }
          }
          update(s => { s.totals.attempts++; if (e.correct) s.totals.correct++; });
        }
        logAttempt(mod.id, e);
      },
      end(sum: RoundSummary) {
        if (ended) return;
        ended = true;
        const reward = roundReward(mod.category, startLevel, sum);
        let gems = reward.gems + doorGems;
        let newStars = 0;
        let jobBonus = 0;
        let gift: string | undefined;
        const eggNotes: string[] = [];
        update(s => {
          if (job) {
            jobBonus = reward.accuracy >= 0.6 ? job.job.bonus : Math.ceil(job.job.bonus / 2);
            gems += jobBonus;
            const day = dayKey();
            (s.town.done[day] ||= []).push(job.key);
            s.town.completed[job.neighbor.id] = (s.town.completed[job.neighbor.id] || 0) + 1;
            gift = giftFor(job.neighbor, s.town.completed[job.neighbor.id], s);
            if (gift) { s.owned.push(gift); s.town.gifts.push(gift); }
            // charms: a region's first quest, and helping every neighbor on the street
            const rid = regionIdFromKey(job.key);
            const earn = (id: string) => { if (!s.charms.includes(id)) { s.charms.push(id); eggNotes.push(`New charm: ${CHARM_BY_ID[id]?.name ?? id}!`); } };
            if (rid && REGION_BY_ID[rid]) earn(REGION_BY_ID[rid].charm);
            if (!rid && friendOfTheStreet(s)) earn('charm.street');
          }
          s.gems += gems;
          s.totals.gemsEarned += gems;
          s.totals.rounds++;
          s.lastPlayed[mod.id] = Date.now();
          if (mod.category !== 'fun' && reward.stars > 0) {
            const g = (s.stars[mod.id] ||= {});
            const key = String(startLevel);
            const prev = g[key] || 0;
            if (reward.stars > prev) { g[key] = reward.stars; newStars = reward.stars - prev; }
          }
          if (mod.category !== 'fun') {
            const ready = warmEggs(s);
            if (ready.length) eggNotes.push('An egg is wobbling in the Nest!');
            const egg = eggForStars(s, totalStars(s));
            if (egg) { s.pets.eggs.push(egg); eggNotes.push(`A ${PET_TYPES[egg.type].name} egg appeared in the Nest (${egg.from}).`); }
          }
        });
        logRound({ game: mod.id, skill: primary ?? '', level: startLevel, attempts: sum.attempts, correct: sum.correct, perfect: sum.perfect, durationMs: sum.durationMs || (performance.now() - t0), gems, stars: reward.stars });
        if (reward.stars >= 2 || mod.category === 'fun' || job) playSound('chime');
        showResults(sum, reward.stars, gems, newStars, moved, jobBonus, gift, eggNotes);
      },
      speak(text) { if (getSave().settings.tts) speak(text); },
      say(text) { msg.textContent = text; msg.className = 'msg'; },
      award(n, reason) {
        if (mod.category !== 'fun' || ended) return;
        if (doorGems >= DOOR_CAP_PER_VISIT * DOOR_GEMS) { toast('That is all the doors for this visit.'); return; }
        const give = Math.min(n, DOOR_GEMS);
        doorGems += give;
        update(s => { s.gems += give; s.totals.gemsEarned += give; });
        toast(`+${give} gems: ${reason}`);
      },
      charm(id) {
        if (getSave().charms.includes(id)) return;
        let egg: ReturnType<typeof eggForCharm> = null;
        update(s => { s.charms.push(id); egg = eggForCharm(s, mod.category); if (egg) s.pets.eggs.push(egg); });
        const c = CHARM_BY_ID[id];
        playSound('chime');
        toast(`New charm: ${c ? c.name : id}`);
        if (egg) setTimeout(() => toast(`An egg appeared in the Nest!`), 2300);
      },
      sound(name) { if (getSave().settings.sound) playSound(name); },
      keep(kind, data) {
        update(s => {
          const list = (s.kept[kind] ||= []);
          list.push({ t: Date.now(), data });
          while (list.length > 24) list.shift();
        });
        toast(kind === 'story' ? 'Saved to your bookshelf.' : kind === 'plot' ? 'Planted in your garden.' : 'Kept in your house.');
      },
    };

    if (job) { msg.textContent = job.job.story; }

    try {
      inst = mod.mount(host, ctx);
    } catch (err) {
      console.error(err);
      host.append(h('p.pad', null, 'This game hit a snag. Tap back and try again.'));
    }
  }

  function pinClamp(r: number, pin?: [number, number]): number {
    if (!pin) return r;
    return Math.max(pin[0], Math.min(pin[1], r));
  }

  function showResults(sum: RoundSummary, stars: number, gems: number, newStars: number, moved: number, jobBonus: number, gift: string | undefined, eggNotes: string[]) {
    const acc = sum.attempts ? Math.round((sum.correct / sum.attempts) * 100) : 0;
    const lines: HTMLElement[] = [];
    if (mod.category !== 'fun') {
      lines.push(h('div.stars', null, [1, 2, 3].map(i => h('div.s' + (i <= stars ? '.on' : '')))));
      lines.push(h('p.center', null, `${sum.correct} of ${sum.attempts} right (${acc}%)`));
    }
    if (gems > 0) lines.push(h('p.center.big.pop', null, `+${gems} `, h('span.dot.gem', { style: { display: 'inline-block', width: '18px', height: '18px' } })));
    if (job) {
      lines.push(h('p.center', { style: { fontWeight: '800' } }, `Job done for ${job.neighbor.name}: +${jobBonus} of that is thanks.`));
      lines.push(h('p.center.muted.small', null, job.job.thanks));
      if (gift) lines.push(h('p.center', { style: { color: 'var(--good)', fontWeight: '800' } }, `${job.neighbor.name} sent a gift for your house: ${DECOR_BY_ID[gift]?.name ?? gift}!`));
    }
    if (newStars > 0) lines.push(h('p.center.muted', null, `+${newStars} new star${newStars > 1 ? 's' : ''} for this game`));
    if (moved > 0) lines.push(h('p.center', { style: { color: 'var(--good)', fontWeight: '800' } }, 'You leveled up!'));
    if (moved < 0) lines.push(h('p.center.muted', null, 'Taking it a bit easier next round.'));
    for (const n of sum.notes || []) lines.push(h('p.center.muted.small', null, n));
    for (const n of eggNotes) lines.push(h('p.center', { style: { color: 'var(--words)', fontWeight: '800' } }, n));
    const sheet = h('div.sheet', null,
      h('h2.center', null, stars === 3 ? 'Perfect!' : stars === 2 ? 'Great round!' : sum.correct > 0 ? 'Nice work!' : 'Good try!'),
      ...lines,
      h('div.grid2', null,
        h('button.btn', { onclick: () => { overlay.remove(); teardown(); go(backTo); } }, regionId ? `Back to ${REGION_BY_ID[regionId].name}` : job ? 'Back to Town' : 'Done'),
        job
          ? h('button.btn.primary', { onclick: () => { overlay.remove(); teardown(); go('house'); } }, gift ? 'See the gift' : 'Go home')
          : h('button.btn.primary', { onclick: () => { overlay.remove(); teardown(); startRound(); } }, 'Play again'),
      ),
    );
    const overlay = h('div.overlay', null, sheet);
    root.append(overlay);
  }

  function teardown() {
    try { inst?.destroy(); } catch { /* ignore */ }
    inst = null;
  }

  startRound();
  return () => teardown();
}
